import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Button,
  Link,
  ToggleButton,
  ToggleButtonGroup,
  Switch,
  Collapse,
} from '@mui/material';

interface AssuranceVisaComponentProps {
  assuranceVisa: string;
  setAssuranceVisa: (value: string) => void;
  sliderValue: boolean;
  setSliderValue: (value: boolean) => void;
}

const AssuranceVisaComponent: React.FC<AssuranceVisaComponentProps> = ({
  assuranceVisa,
  setAssuranceVisa,
  sliderValue,
  setSliderValue,
}) => {
  const { t } = useTranslation('formulaires');
  const [showConditions, setShowConditions] = useState<boolean>(false);

  const handleToggle = (
    event: React.MouseEvent<HTMLElement>,
    newValue: string | null
  ) => {
    if (newValue !== null) {
      setAssuranceVisa(newValue);
      if (newValue === 'non') {
        setSliderValue(false);
      }
    }
  };

  const handleShowConditions = (e: React.MouseEvent) => {
    e.preventDefault();
    const blockBeforeUnload = (event: BeforeUnloadEvent) => {
      event.stopImmediatePropagation();
    };
    window.addEventListener('beforeunload', blockBeforeUnload, true);
    setShowConditions(true);
    setTimeout(() => {
      window.removeEventListener('beforeunload', blockBeforeUnload, true);
    }, 100);
  };

  return (
    <Box sx={{ mb: 3, p: { xs: 2, sm: 3 } }}>
      {/* Encart dépliable pour afficher les conditions */}
      <Collapse in={showConditions}>
        <Box
          sx={{
            border: '1px solid #ccc',
            borderRadius: 2,
            p: 2,
            mb: 2,
            backgroundColor: '#f9f9f9',
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
            {t('insurance.cgu.title')}
          </Typography>
          <Typography
            variant="body1"
            sx={{ textAlign: 'justify', whiteSpace: 'pre-line', mb: 2 }}
          >
            {t('insurance.cgu.text')}
          </Typography>
          <Button
            variant="outlined"
            onClick={() => setShowConditions(false)}
            sx={{ display: 'block', mx: 'auto' }}
            type="button"
          >
            {t('insurance.cgu.close')}
          </Button>
        </Box>
      </Collapse>

      {/* Titre et description */}
      <Typography
        variant="h6"
        align="center"
        sx={{ fontWeight: 'bold', mt: 2, mb: 1 }}
      >
        {t('insurance.title')}
      </Typography>
      <Typography
        variant="body2"
        align="center"
        sx={{ color: 'gray', mb: 2 }}
      >
        {t('insurance.subtitle')}
      </Typography>

      {/* Boutons de sélection Oui/Non */}
      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mb: 2 }}>
        <ToggleButtonGroup
          value={assuranceVisa}
          exclusive
          onChange={handleToggle}
          aria-label={t('insurance.ariaLabel.group')}
        >
          <ToggleButton
            value="oui"
            aria-label={t('insurance.ariaLabel.yes')}
            sx={{
              textTransform: 'none',
              fontSize: 16,
              '&.Mui-selected': {
                backgroundColor: '#3eab36',
                color: 'white',
              },
            }}
          >
            {t('insurance.yes')}
          </ToggleButton>
          <ToggleButton
            value="non"
            aria-label={t('insurance.ariaLabel.no')}
            sx={{
              textTransform: 'none',
              fontSize: 16,
              '&.Mui-selected': {
                backgroundColor: '#3eab36',
                color: 'white',
              },
            }}
          >
            {t('insurance.no')}
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Liens vers les documents */}
      <Box sx={{ textAlign: 'center', mb: 2 }}>
        <Link
          href="https://visamonde.com/chapka/Tableau%20des%20garanties%20assurance%20visa.pdf"
          target="_blank"
          underline="hover"
          sx={{ display: 'block', mb: 1, fontSize: 16 }}
        >
          {t('insurance.links.guarantees')}
        </Link>
        <Link
          href="https://visamonde.com/chapka/CGV%20ass%20visa.pdf"
          target="_blank"
          underline="hover"
          sx={{ display: 'block', mb: 1, fontSize: 16 }}
        >
          {t('insurance.links.terms')}
        </Link>
        <Link
          href="https://visamonde.com/chapka/IPID%20assurance%20visa.pdf"
          target="_blank"
          underline="hover"
          sx={{ display: 'block', mb: 1, fontSize: 16 }}
        >
          {t('insurance.links.ipid')}
        </Link>
      </Box>

      {/* Section Switch pour accepter les conditions */}
      <Box sx={{ textAlign: 'center', mb: 2 }}>
        <Typography variant="body1" sx={{ mb: 1 }}>
          {t('insurance.acceptance.prefix')}
          <Link
            component="button"
            type="button"
            variant="body1"
            onClick={handleShowConditions}
            sx={{ textDecoration: 'underline', color: '#0645AD' }}
          >
            {t('insurance.acceptance.linkText')}
          </Link>
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <Switch
            checked={sliderValue}
            onChange={(e) => setSliderValue(e.target.checked)}
            color="primary"
          />
          <Typography variant="body1" sx={{ ml: 1 }}>
            {t('insurance.acceptance.yes')}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default AssuranceVisaComponent;
